import { useEffect, useRef, useState } from 'react';
import type React from 'react';
import { useAmbientAudio, type AmbientSource } from './useAmbientAudio';

function useReducedMotion(): boolean {
  const [reduce, setReduce] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const m = window.matchMedia('(prefers-reduced-motion: reduce)');
    const handle = () => setReduce(m.matches);
    handle();
    m.addEventListener?.('change', handle);
    return () => m.removeEventListener?.('change', handle);
  }, []);
  return reduce;
}

function useInView(ref: React.RefObject<Element | null>): boolean {
  const [inView, setInView] = useState(true);
  useEffect(() => {
    if (!ref.current || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver((entries) => {
      setInView(!!entries[0]?.isIntersecting);
    }, { rootMargin: '50px' });
    io.observe(ref.current);
    return () => io.disconnect();
  }, [ref]);
  return inView;
}

interface Props {
  audio: AmbientSource | null;
}

type PetState = 'off' | 'on' | 'reacting';

export default function PetCompanion({ audio }: Props) {
  const { on, toggle, available, audioRef, isFile, src } = useAmbientAudio(audio);
  const [reacting, setReacting] = useState(false);
  const reactTimeoutRef = useRef<number | null>(null);
  const svgRef = useRef<SVGSVGElement | null>(null);
  const reducedMotion = useReducedMotion();
  const visible = useInView(svgRef);

  useEffect(() => () => {
    if (reactTimeoutRef.current !== null) window.clearTimeout(reactTimeoutRef.current);
  }, []);

  if (!audio) return null;
  if (!available) return null;

  const state: PetState = reacting ? 'reacting' : on ? 'on' : 'off';

  const handleToggle = () => {
    // Show a brief reacting wiggle on every tap, then toggle audio.
    toggle();
    setReacting(true);
    if (reactTimeoutRef.current !== null) window.clearTimeout(reactTimeoutRef.current);
    reactTimeoutRef.current = window.setTimeout(() => setReacting(false), 500);
  };

  return (
    <div className="fixed -bottom-2 left-2 sm:-bottom-1 sm:left-2 z-[400] pointer-events-none" aria-live="off">
      {isFile && (
        <audio ref={audioRef} src={src} loop preload="none" aria-hidden="true" />
      )}
      <button
        type="button"
        className="pet-button pointer-events-auto"
        aria-label={on ? 'Couper la musique' : 'Activer la musique'}
        aria-pressed={on}
        onClick={handleToggle}
      >
        <div className="pet-stage" data-state={state} data-visible={visible ? 'true' : 'false'}>
          {/* Exact cat-on-boombox artwork. The eyes and the left front paw
              are painted out of this raster and redrawn as separate layers
              (SVG eyes below, paw sprite here) so they can animate. */}
          <img
            className="pet-img"
            src="/img/pet-cat-boombox.png"
            alt=""
            aria-hidden="true"
            draggable={false}
          />

          {/* Left front paw — its own sprite, registered 1:1 over the artwork,
              waves/taps. It only ever rotates inward (over the white body) so
              the gap it uncovers always falls on the background side. */}
          <img
            className="pet-paw"
            src="/img/pet-cat-paw.png"
            alt=""
            aria-hidden="true"
            draggable={false}
          />

          {/* Overlay: animatable eyes (registered to the artwork) + music notes.
              viewBox matches the 480x410 artwork so eye coords map 1:1.
              data-pet/data-state/data-visible mirror the stage so existing
              tests that query svg[data-pet] keep working. */}
          <svg
            ref={svgRef}
            data-pet
            data-state={state}
            data-visible={visible ? 'true' : 'false'}
            data-reduced-motion={reducedMotion ? 'true' : 'false'}
            viewBox="0 0 480 410"
            className="pet-overlay"
            aria-hidden="true"
          >
            {/* eyes — open (idle blink + playful wink) */}
            <g className="pet-eye pet-eye-l">
              <ellipse cx="164" cy="152" rx="13.5" ry="14" fill="#43302f" />
            </g>
            <g className="pet-eye pet-eye-r">
              <ellipse cx="304" cy="169" rx="13.5" ry="13.5" fill="#43302f" />
            </g>

            {/* happy "^^" eyes — shown while music plays */}
            <g className="pet-eye-happy">
              <path d="M 148 159 Q 164 137 180 159" stroke="#43302f" strokeWidth="8" strokeLinecap="round" fill="none" />
              <path d="M 288 176 Q 304 154 320 176" stroke="#43302f" strokeWidth="8" strokeLinecap="round" fill="none" />
            </g>

            {/* notes — radiate out from the boombox while music plays */}
            <g className="pet-notes" transform="translate(238 210) scale(3.6)">
              <text className="pet-note pet-note-1" x="0" y="0" fontSize="22" fill="var(--magenta)" textAnchor="middle">♪</text>
              <text className="pet-note pet-note-2" x="0" y="0" fontSize="20" fill="var(--hot-pink)" textAnchor="middle">♫</text>
              <text className="pet-note pet-note-3" x="0" y="0" fontSize="24" fill="var(--magenta)" textAnchor="middle">♩</text>
              <text className="pet-note pet-note-4" x="0" y="0" fontSize="18" fill="var(--hot-pink)" textAnchor="middle">♬</text>
              <text className="pet-note pet-note-5" x="0" y="0" fontSize="20" fill="var(--magenta)" textAnchor="middle">♥</text>
              <text className="pet-note pet-note-6" x="0" y="0" fontSize="22" fill="var(--hot-pink)" textAnchor="middle">♪</text>
              <text className="pet-note pet-note-7" x="0" y="0" fontSize="19" fill="var(--magenta)" textAnchor="middle">♫</text>
              <text className="pet-note pet-note-8" x="0" y="0" fontSize="21" fill="var(--hot-pink)" textAnchor="middle">★</text>
            </g>
          </svg>
        </div>
      </button>

      <style>{`
        .pet-button {
          appearance: none;
          background: transparent;
          border: 0;
          padding: 0;
          margin: 0;
          cursor: pointer;
          border-radius: 50%;
          -webkit-tap-highlight-color: transparent;
        }
        .pet-button:focus-visible {
          outline: 2px solid var(--ink, currentColor);
          outline-offset: 4px;
        }
        .pet-stage {
          position: relative;
          display: block;
          transform-origin: 50% 100%;
          filter: drop-shadow(0 3px 6px rgba(0,0,0,0.18));
          opacity: 0.88;
        }
        .pet-img {
          display: block;
          width: 84px;
          height: auto;
          user-select: none;
          -webkit-user-drag: none;
        }
        .pet-overlay {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          overflow: visible;
          pointer-events: none;
        }
        .pet-paw {
          position: absolute;
          inset: 0;
          width: 100%;
          height: auto;
          /* pivot at the paw's top tip — the tip stays put while the rounded
             mitten swings, so no outline ever pokes past the silhouette */
          transform-origin: 27.5% 12.9%;
          animation: pet-paw-wave 3s ease-in-out infinite;
          user-select: none;
          -webkit-user-drag: none;
        }
        .pet-eye-l { transform-origin: 164px 152px; }
        .pet-eye-r { transform-origin: 304px 169px; }
        .pet-eye-happy { opacity: 0; }
        .pet-notes { opacity: 0; }
        .pet-note { transform-origin: center; opacity: 0; }

        /* OFF: gentle bob + natural playful blink-wink */
        .pet-stage[data-state="off"] { animation: pet-bob 4.5s ease-in-out infinite; }
        .pet-stage[data-state="off"] .pet-eye-l { animation: pet-blink-l 5.4s ease-in-out infinite; }
        .pet-stage[data-state="off"] .pet-eye-r { animation: pet-blink-r 5.4s ease-in-out infinite; }

        /* ON: squish to the beat, soft glow, happy "^^" eyes, notes burst */
        .pet-stage[data-state="on"] { animation: pet-squish 0.55s ease-in-out infinite, pet-glow 1.1s ease-in-out infinite; }
        .pet-stage[data-state="on"] .pet-eye { opacity: 0; }
        .pet-stage[data-state="on"] .pet-eye-happy { opacity: 1; }
        .pet-stage[data-state="on"] .pet-notes { opacity: 1; }
        .pet-stage[data-state="on"] .pet-note-1 { animation: pet-note-burst-up    2.8s ease-out 0.00s infinite; }
        .pet-stage[data-state="on"] .pet-note-2 { animation: pet-note-burst-ne    2.8s ease-out 0.35s infinite; }
        .pet-stage[data-state="on"] .pet-note-3 { animation: pet-note-burst-e     2.8s ease-out 0.70s infinite; }
        .pet-stage[data-state="on"] .pet-note-4 { animation: pet-note-burst-nw    2.8s ease-out 1.05s infinite; }
        .pet-stage[data-state="on"] .pet-note-5 { animation: pet-note-burst-w     2.8s ease-out 1.40s infinite; }
        .pet-stage[data-state="on"] .pet-note-6 { animation: pet-note-burst-up-l  2.8s ease-out 1.75s infinite; }
        .pet-stage[data-state="on"] .pet-note-7 { animation: pet-note-burst-up-r  2.8s ease-out 2.10s infinite; }
        .pet-stage[data-state="on"] .pet-note-8 { animation: pet-note-burst-up    2.8s ease-out 2.45s infinite; }
        .pet-stage[data-state="on"] .pet-paw { animation: pet-paw-tap 0.55s ease-in-out infinite; }

        /* REACTING: one-shot bounce on any tap */
        .pet-stage[data-state="reacting"] { animation: pet-jump 0.5s ease-out 1; }

        @keyframes pet-bob {
          0%, 100% { transform: translateY(0) rotate(-1deg); }
          50%      { transform: translateY(-3px) rotate(1deg); }
        }
        @keyframes pet-squish {
          0%, 100% { transform: scaleY(1)    scaleX(1); }
          50%      { transform: scaleY(0.94) scaleX(1.045); }
        }
        @keyframes pet-glow {
          0%, 100% { filter: drop-shadow(0 3px 6px rgba(0,0,0,0.18)) drop-shadow(0 0 0 transparent); }
          50%      { filter: drop-shadow(0 3px 6px rgba(0,0,0,0.18)) drop-shadow(0 0 9px var(--magenta)); }
        }
        @keyframes pet-jump {
          0%   { transform: translateY(0)    scaleX(1)    scaleY(1); }
          25%  { transform: translateY(-9px) scaleX(0.92) scaleY(1.1); }
          55%  { transform: translateY(0)    scaleX(1.1)  scaleY(0.9); }
          80%  { transform: translateY(-3px) scaleX(0.98) scaleY(1.02); }
          100% { transform: translateY(0)    scaleX(1)    scaleY(1); }
        }
        /* Paw — rotation stays <= 0 (swings the mitten inward, over the white
           body) so the gap it uncovers always lands on the background side. */
        @keyframes pet-paw-wave {
          0%, 100% { transform: rotate(0deg); }
          50%      { transform: rotate(-2.5deg); }
        }
        @keyframes pet-paw-tap {
          0%   { transform: rotate(0deg); }
          22%  { transform: rotate(-5deg); }
          44%  { transform: rotate(-1deg); }
          64%  { transform: rotate(-4deg); }
          84%  { transform: rotate(-0.5deg); }
          100% { transform: rotate(0deg); }
        }
        /* Natural playful blink-wink — both eyes blink in sync at 17% and 74%;
           the right eye also winks alone at 46% for a mischievous beat.
           Quick close with a small upward shift to read as a happy squint. */
        @keyframes pet-blink-l {
          0%, 14%   { transform: scaleY(1)    translateY(0); }
          17%       { transform: scaleY(0.05) translateY(-6px); }
          20%       { transform: scaleY(1)    translateY(0); }
          70%       { transform: scaleY(1)    translateY(0); }
          74%       { transform: scaleY(0.05) translateY(-6px); }
          82%       { transform: scaleY(1)    translateY(0); }
          100%      { transform: scaleY(1)    translateY(0); }
        }
        @keyframes pet-blink-r {
          0%, 14%   { transform: scaleY(1)    translateY(0); }
          17%       { transform: scaleY(0.05) translateY(-6px); }
          20%       { transform: scaleY(1)    translateY(0); }
          42%       { transform: scaleY(1)    translateY(0); }
          46%       { transform: scaleY(0.05) translateY(-8px); }
          52%       { transform: scaleY(1)    translateY(0); }
          70%       { transform: scaleY(1)    translateY(0); }
          74%       { transform: scaleY(0.05) translateY(-6px); }
          82%       { transform: scaleY(1)    translateY(0); }
          100%      { transform: scaleY(1)    translateY(0); }
        }
        /* radiating notes — all outward & upward, dramatic expansion, gradual fade */
        @keyframes pet-note-burst-up {
          0%   { opacity: 0;   transform: translate(0, -2px)   scale(0.3) rotate(-10deg); }
          18%  { opacity: 1;   transform: translate(0, -22px)  scale(1.1) rotate(0deg);   }
          65%  { opacity: 0.7; transform: translate(0, -56px)  scale(1.8) rotate(8deg);   }
          100% { opacity: 0;   transform: translate(0, -90px)  scale(2.6) rotate(18deg);  }
        }
        @keyframes pet-note-burst-ne {
          0%   { opacity: 0;   transform: translate(2px, -2px)   scale(0.3) rotate(-12deg); }
          18%  { opacity: 1;   transform: translate(14px, -20px) scale(1.1) rotate(0deg);   }
          65%  { opacity: 0.7; transform: translate(38px, -50px) scale(1.8) rotate(14deg);  }
          100% { opacity: 0;   transform: translate(60px, -78px) scale(2.4) rotate(26deg);  }
        }
        @keyframes pet-note-burst-e {
          0%   { opacity: 0;   transform: translate(2px, -2px)   scale(0.3) rotate(-6deg); }
          18%  { opacity: 1;   transform: translate(22px, -10px) scale(1.1) rotate(0deg);  }
          65%  { opacity: 0.7; transform: translate(54px, -32px) scale(1.8) rotate(12deg); }
          100% { opacity: 0;   transform: translate(80px, -56px) scale(2.4) rotate(22deg); }
        }
        @keyframes pet-note-burst-nw {
          0%   { opacity: 0;   transform: translate(-2px, -2px)   scale(0.3) rotate(12deg);  }
          18%  { opacity: 1;   transform: translate(-14px, -20px) scale(1.1) rotate(0deg);   }
          65%  { opacity: 0.7; transform: translate(-38px, -50px) scale(1.8) rotate(-14deg); }
          100% { opacity: 0;   transform: translate(-60px, -78px) scale(2.4) rotate(-26deg); }
        }
        @keyframes pet-note-burst-w {
          0%   { opacity: 0;   transform: translate(-2px, -2px)   scale(0.3) rotate(6deg);  }
          18%  { opacity: 1;   transform: translate(-22px, -10px) scale(1.1) rotate(0deg);  }
          65%  { opacity: 0.7; transform: translate(-54px, -32px) scale(1.8) rotate(-12deg); }
          100% { opacity: 0;   transform: translate(-80px, -56px) scale(2.4) rotate(-22deg); }
        }
        @keyframes pet-note-burst-up-l {
          0%   { opacity: 0;   transform: translate(-1px, -2px)  scale(0.3) rotate(-8deg);  }
          18%  { opacity: 1;   transform: translate(-8px, -22px) scale(1.1) rotate(0deg);   }
          65%  { opacity: 0.7; transform: translate(-22px, -56px) scale(1.9) rotate(-12deg); }
          100% { opacity: 0;   transform: translate(-34px, -88px) scale(2.6) rotate(-22deg); }
        }
        @keyframes pet-note-burst-up-r {
          0%   { opacity: 0;   transform: translate(1px, -2px)  scale(0.3) rotate(8deg);  }
          18%  { opacity: 1;   transform: translate(8px, -22px) scale(1.1) rotate(0deg);  }
          65%  { opacity: 0.7; transform: translate(22px, -56px) scale(1.9) rotate(12deg); }
          100% { opacity: 0;   transform: translate(34px, -88px) scale(2.6) rotate(22deg); }
        }

        @media (prefers-reduced-motion: reduce) {
          .pet-stage, .pet-eye-l, .pet-eye-r, .pet-notes, .pet-note, .pet-paw {
            animation: none !important;
          }
          .pet-notes { opacity: 0; }
        }

        .pet-stage[data-visible="false"] { animation-play-state: paused !important; }
        .pet-stage[data-visible="false"] .pet-overlay *,
        .pet-stage[data-visible="false"] .pet-paw { animation-play-state: paused !important; }
      `}</style>
    </div>
  );
}
